import { QRCodeCanvas } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';

interface QrCodeImageProps {
  /** The value (URL) encoded into the QR code. */
  value: string;
  /** Rendered width/height in pixels. */
  size: number;
  /** Error-correction level. Higher = more redundancy, denser code. */
  level?: 'L' | 'M' | 'Q' | 'H';
  /** Accessible alt text for the image. */
  alt?: string;
  className?: string;
}

/**
 * Renders a QR code as a plain `<img>` (PNG) rather than a `<canvas>`.
 *
 * A `<canvas>` only exposes "Inspect" in the browser context menu, whereas an
 * `<img>` gives the native "Save Image As…" / "Copy Image" / "Open image in new
 * tab" actions — which is what we want for downloadable, scannable QR codes. A
 * hidden `<canvas>` is used purely to rasterize the QR into a data URL.
 *
 * @param props - The QR value, size, and presentation options.
 * @returns An `<img>` element once the QR has been rasterized.
 */
export function QrCodeImage({ value, size, level = 'M', alt = 'QR code', className }: QrCodeImageProps) {
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    // QRCodeCanvas (a child) has already drawn by the time this parent effect
    // runs, so the backing canvas is ready to export — even though it's hidden.
    const canvas = canvasHostRef.current?.querySelector('canvas');
    if (canvas) {
      setDataUrl(canvas.toDataURL('image/png'));
    }
  }, [value, size, level]);

  return (
    <>
      <div ref={canvasHostRef} className="hidden" aria-hidden="true">
        <QRCodeCanvas value={value} size={size} level={level} marginSize={0} />
      </div>
      {dataUrl ? (
        <img src={dataUrl} alt={alt} width={size} height={size} className={className} />
      ) : null}
    </>
  );
}
